import React from 'react';
import COInlineMessage from '@components/ringil3/COInlineMessage/COInlineMessage.tsx';
import SvgAlert from '@icons/alert-circle.svg?react';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import { ReservationUnavailabilityOutDTO, UnavailabilityReasonEnum } from '@api/ringil-api.v2';

/**
 * Fallbacks used when Locize does not know the key the backend sent yet, so the user still gets
 * something better than the raw key. The backend translation always wins when it exists.
 */
const REASON_FALLBACK: Partial<Record<UnavailabilityReasonEnum, string>> = {
  [UnavailabilityReasonEnum.NEXT_DAY_DEADLINE]: 'The deadline for booking this day has already passed.',
  [UnavailabilityReasonEnum.SLOT_IN_PAST]: 'This time has already passed.',
  [UnavailabilityReasonEnum.LOCATION_CLOSED]: 'The location is closed on this day.',
  [UnavailabilityReasonEnum.NO_OPENING_HOURS]: 'No opening hours are set for this day.',
  [UnavailabilityReasonEnum.OUTSIDE_OPENING_HOURS]: 'The requested time is outside the opening hours.',
  [UnavailabilityReasonEnum.SLOT_OCCUPIED]: 'All time slots for this day are already taken.',
  [UnavailabilityReasonEnum.DAILY_RESERVATION_LIMIT]: 'The daily limit of reservations has been reached.',
  [UnavailabilityReasonEnum.DAILY_SKU_LIMIT]: 'The daily limit of goods has been reached.',
  [UnavailabilityReasonEnum.WEEKLY_RESERVATION_LIMIT]: 'The weekly limit of reservations has been reached.',
  [UnavailabilityReasonEnum.WEEKLY_SKU_LIMIT]: 'The weekly limit of goods has been reached.',
};

/** Positional params from the backend are exposed to i18next as {{0}}, {{1}}, ... */
const interpolationData = (params: string[]) => Object.fromEntries((params ?? []).map((value, index) => [index, value]));

interface ComponentProps {
  /** Reason returned by the free-slots endpoint; when missing the generic message is shown. */
  unavailability?: ReservationUnavailabilityOutDTO;
}

/**
 * Explains why a ramp (or a whole day) offers no time slots. Shared by every place that renders
 * the "offer available slots" panel.
 */
const NoAvailableSlotsMessage: React.FC<ComponentProps> = ({ unavailability }) => {
  const { tr } = useTranslationLgs();

  const message = unavailability?.messageKey
    ? tr(
        unavailability.messageKey,
        REASON_FALLBACK[unavailability.unavailabilityReason] ?? tr('DuplicateExpeditionSidebar.noAvailableSlots', 'No time slot available. Choose another day or contact the location owner.'),
        interpolationData(unavailability.params),
      )
    : tr('DuplicateExpeditionSidebar.noAvailableSlots', 'No time slot available. Choose another day or contact the location owner.');

  // The key the text above came from, for the AI assistant: the backend's reason key with its params
  // and reason code, or the generic one.
  const diagnostic = unavailability?.messageKey
    ? { key: unavailability.messageKey, params: unavailability.params ?? [], code: unavailability.unavailabilityReason ?? null }
    : { key: 'DuplicateExpeditionSidebar.noAvailableSlots' };

  return (
    <COInlineMessage variant={'warning'} icon={<SvgAlert className="text-content-tertiary" />} diagnostic={diagnostic}>
      {message}
    </COInlineMessage>
  );
};

export default NoAvailableSlotsMessage;
